import React, { useState, useEffect } from 'react';
// import { Dialog, DialogContent, DialogTitle, DialogHeader } from '@/components/ui/dialog';
import { qryCopilotHistoryInsightsApi } from '@/api/copilot';
import { EXTERNAL_USER_ID } from '@/config/env';
import { createPortal } from 'react-dom';
import { ChevronLeft } from 'lucide-react';
import dayjs from 'dayjs';
import { Button } from '@/components/ui/button';
import { RotateCcw } from 'lucide-react';
import { isCanParseJson } from '@/lib/utils';
// 简单的 Skeleton 组件
const SkeletonDashboard: React.FC = () => (
  <div className="space-y-4">
    {[1, 2].map(i => (
      <div key={i} className="rounded-lg bg-gray-100 p-3">
        <div className="mb-2 flex items-center justify-between">
          <div className="h-4 w-20 rounded bg-gray-200"></div>
          <div className="h-4 w-16 rounded bg-gray-200"></div>
        </div>
        <div className="mb-2 h-4 w-full rounded bg-gray-200"></div>
        <div className="h-12 w-full rounded bg-gray-200"></div>
      </div>
    ))}
  </div>
);

interface HistoryInsight {
  id: string;
  type: '产品推荐' | '市场解读' | '资产配置';
  status: '已采纳' | '执行中' | '待处理';
  triggerDialog: string;
  insightContent: string;
  createTime: string;
  assetJson: {
    return_res: string;
    pullback: string;
    amout: number;
  };
}

interface HistoryInsightsDialogProps {
  setOpen: (open: boolean) => void;
  followUserId: string;
  handleReapply: (assetJson: HistoryInsight['assetJson']) => void;
}

const HistoryInsightsDialog: React.FC<HistoryInsightsDialogProps> = ({
  setOpen,
  followUserId,
  handleReapply
}) => {
  const [insights, setInsights] = useState<HistoryInsight[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    qryCopilotHistoryInsightsApi({
      externalUserId: EXTERNAL_USER_ID,
      followUserId: followUserId
    })
      .then(res => {
        try {
          const result = (res || []).map(item => ({
            ...item,
            assetJson: isCanParseJson(item.assetJson) ? JSON.parse(item.assetJson) : item.assetJson
          }));
          console.log('result', result);
          setInsights(result);
        } catch (error) {
          console.log(error);
        }
      })
      .catch(err => {
        console.log(err);
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  //   const getStatusColor = (status: string) => {
  //     switch (status) {
  //       case '已采纳':
  //         return 'bg-[#CAD0FB] text-[#1F1F1F]';
  //       case '执行中':
  //         return 'bg-[#CAD0FB] text-[#1F1F1F]';
  //       case '待处理':
  //         return 'bg-gray-100 text-gray-600';
  //       default:
  //         return 'bg-gray-100 text-gray-600';
  //     }
  //   };

  //   const getTypeColor = () => {
  //     return 'bg-[#5959EB] text-white';
  //   };

  return createPortal(
    <div className="fixed inset-0 bottom-0 top-0 z-[1111111] min-h-screen pb-4">
      <div className="flex h-full flex-col bg-white px-3">
        <div className="relative my-6 w-full">
          <div
            className="absolute left-0 top-0 flex h-7 cursor-pointer items-center"
            onClick={() => setOpen(false)}
          >
            <ChevronLeft size={16} />
          </div>
          <div className="text-center text-xl font-semibold text-gray-900">历史方案</div>
        </div>
        {/* 内容区域 */}
        <div
          className="h-[calc(100vh - 28px - 48px)] space-y-4 overflow-y-scroll"
          style={{ height: 'calc(100vh - 28px - 48px)' }}
        >
          {loading ? (
            <SkeletonDashboard />
          ) : insights.length === 0 ? (
            <div className="py-8 text-center text-gray-500">暂无历史方案</div>
          ) : (
            <div>
              {insights.map(insight => (
                <div
                  key={insight.id}
                  className="mb-3 rounded-lg bg-gradient-to-br from-[#EEEDFF] to-[#F6F7FF] p-3"
                >
                  {/* 头部信息 */}
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {/* <Badge className={getTypeColor()}>{insight.type}</Badge> */}
                      <span className="whitespace-nowrap text-sm text-[#1F1F1F]">
                        {dayjs(insight.createTime).format('YYYY-MM-DD HH:mm:ss')}
                      </span>
                      <span className="ml-1 whitespace-nowrap text-sm text-[#1F1F1F]">
                        配置方案
                      </span>
                    </div>

                    {/* 操作按钮 */}
                    <div className="flex items-center justify-between">
                      <div className="flex gap-4">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="flex h-auto items-center gap-1 p-0 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
                          onClick={() =>
                            handleReapply(
                              insight.assetJson
                                ? insight.assetJson
                                : ({} as HistoryInsight['assetJson'])
                            )
                          }
                        >
                          <RotateCcw className="h-4 w-4" />
                          重新应用
                        </Button>
                      </div>
                    </div>
                    {/* <Badge className={getStatusColor(insight.status)}>{insight.status}</Badge> */}
                  </div>

                  {/* 洞察内容 */}
                  <div className="mb-2 rounded-md bg-[rgba(202,208,251,0.3)] p-2">
                    <div className="grid grid-cols-1 gap-2 xs-400:grid-cols-3">
                      <div className="flex items-center gap-2">
                        <span className="whitespace-nowrap text-sm text-[#1F1F1F]">
                          期望收益率:
                        </span>
                        <span className="whitespace-nowrap text-sm text-[#5959EB]">
                          {typeof insight.assetJson === 'object'
                            ? insight.assetJson.return_res
                            : null}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="whitespace-nowrap text-sm text-[#1F1F1F]">最大回撤:</span>
                        <span className="whitespace-nowrap text-sm text-[#5959EB]">
                          {typeof insight.assetJson === 'object'
                            ? insight.assetJson.pullback
                            : null}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="whitespace-nowrap text-sm text-[#1F1F1F]">投资金额:</span>
                        <span className="whitespace-nowrap text-sm text-[#5959EB]">
                          {typeof insight.assetJson === 'object' ? insight.assetJson.amout : null}万
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.querySelector('#root')
  );
};

export default HistoryInsightsDialog;
